import {
  DailyRecurrence,
  HourlyRecurrence,
  MonthlyRecurrence,
  Recurrence,
  RecurrenceDetails,
  RecurrenceModes,
  RecurrenceTypes,
  SelfServiceSchedulePaneProps,
  StaticInitializers,
  TimeZoneDetails,
  WeeklyRecurrence,
} from '@tableau/self-service-schedules';
import { SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { SiteUrls } from '@tableau/shared-urls';

import { ITypeaheadSource } from '../directives/tbTypeahead';
import { FeatureFlags } from '../FeatureFlags';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode, SchedulingErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { IExtractTaskInfo } from '../models/IExtractTaskInfo';
import { ISubscriptionInfo, SubscriptionTargetTypes } from '../models/ISubscriptionInfo';
import { IErrorItem } from '../models/MultipleItemResult';
import { INamedItemInfo, IScheduledItemInfo } from '../models/resourceInfoParts';
import { RunFlowTaskInfo } from '../models/RunFlowTaskInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { ExtractTasks } from '../resources/ExtractTasks';
import { RunFlowTasks } from '../resources/RunFlowTasks';
import { Subscriptions } from '../resources/Subscriptions';
import { IResult } from '../ServerApi/types/IResponse';
import { DefinedBy, ISchedule, ISelfServiceScheduleDetails, ScheduleActions } from '../ServerApi/types/ISchedule';
import { ScheduleDetails } from '../ServerApi/types/ScheduleDetails';
import { scheduledActionEnum, scheduledTypeEnum, SelfServiceSchedule } from '../ServerApi/types/SelfServiceSchedule';
import { ActionHelpers } from './ActionHelpers';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { IErrorDetail, INotificationTranslationIds } from './ContentActionNotification';
import { SearchSuggestionsService } from './SearchSuggestionsService';
import { ServerService } from './ServerService';
import { ToasterService, ToastType } from './ToasterService';

interface IChangeScheduleActionOptions<T> {
  notificationTranslationIds: INotificationTranslationIds;
  scheduledActionType: string;
  descriptionTranslateKey: string;
  descriptionTranslateValues: _.Dictionary<any>;
}

interface IChangeAdminScheduleActionOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<T>) => IErrorDetail;
  action: (ids: string[], scheduleId: string) => ng.IPromise<IResult>;
}

interface IChangeSubscriptionsFrequencyOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<ISubscriptionInfo>) => IErrorDetail;
  action: (ids: string[], scheduleId: string, subscribeOnExtractRefresh: boolean) =>
    ng.IPromise<IResult>;
}

interface IChangeSelfServiceSubscriptionsFrequencyOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: () => (errorItem: IErrorItem<ISubscriptionInfo>) => IErrorDetail;
  action: (ids: string[], schedule: SelfServiceSchedule, subscribeOnExtractRefresh: boolean) =>
    ng.IPromise<IResult>;
}

interface IChangeSelfServiceScheduleActionOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: () => (errorItem: IErrorItem<T>) => IErrorDetail;
  action: (ids: string[], schedule: SelfServiceSchedule) => ng.IPromise<IResult>;
}

export enum SubscriptionsFrequency {
  subscribeOnSelectedSchedule = 1 << 0,
  subscribeOnExtractRefresh = 1 << 1
}

export interface IChangeScheduleScope extends ng.IScope {
  scheduleSource: ITypeaheadSource;
  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  filterList: {
    model: ISchedule
  };
}

export interface IChangeSubscriptionsFrequencyScope extends ng.IScope {
  scheduleSource: ITypeaheadSource;
  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  filterList: {
    model: ISchedule
  };
  frequencyOptions: {
    'id': SubscriptionsFrequency; 'label': string;
  }[];
  selected: {
    id: SubscriptionsFrequency;
  };
}

export interface IChangeSubscriptionsFrequencyScope extends ng.IScope {
  scheduleSource: ITypeaheadSource;
  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  filterList: {
    model: ISchedule
  };
  frequencyOptions: {
    'id': SubscriptionsFrequency; 'label': string;
  }[];
  selected: {
    id: SubscriptionsFrequency;
  };
}

export interface IChangeSubscriptionsFrequencyScope extends ng.IScope {
  scheduleSource: ITypeaheadSource;
  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  filterList: {
    model: ISchedule
  };
  frequencyOptions: {
    'id': SubscriptionsFrequency; 'label': string;
  }[];
  selected: {
    id: SubscriptionsFrequency;
  };
}

export interface IChangeSubscriptionsFrequencyScope extends ng.IScope {
  scheduleSource: ITypeaheadSource;
  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  filterList: {
    model: ISchedule
  };
  frequencyOptions: {
    'id': SubscriptionsFrequency; 'label': string;
  }[];
  selected: {
    id: SubscriptionsFrequency;
  };
}


export interface IChangeSelfServiceScheduleScope extends ng.IScope {
  selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps;

  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
}

export interface IChangeSelfServiceSubscriptionsFrequencyScope extends ng.IScope {
  selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps;

  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  frequencyOptions: {
    'id': SubscriptionsFrequency; 'label': string;
  }[];
  selected: {
    id: SubscriptionsFrequency;
  };
}

export class ChangeScheduleAction {

  public static $inject = [
    '$translate',
    'ActionHelpers',
    'ConfirmActionDialog',
    'ExtractTasks',
    'RunFlowTasks',
    'scheduleFrequencyDescriptionFilter',
    'SearchSuggestionsService',
    'ServerService',
    'ToasterService',
    'Subscriptions'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private ActionHelpers: ActionHelpers,
    private ConfirmActionDialog: ConfirmActionDialog,
    private ExtractTasks: ExtractTasks,
    private RunFlowTasks: RunFlowTasks,
    private scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
    private SearchSuggestionsService: SearchSuggestionsService,
    private server: ServerService,
    private toaster: ToasterService,
    private Subscriptions: Subscriptions
  ) { }

  // Generic logic

  private createDialogScope<T>(parentScope: ng.IScope,
    items: Set<T>,
    options: IChangeScheduleActionOptions<T>): IChangeScheduleScope {

    const scope = <IChangeScheduleScope>parentScope.$new();

    scope.descriptionTranslateKey = options.descriptionTranslateKey;
    scope.descriptionTranslateValues = options.descriptionTranslateValues;

    scope.scheduleSource = this.SearchSuggestionsService.schedulesSource(options.scheduledActionType);
    scope.filterList = {
      model: null
    };

    scope.itemTextFn = (schedule: ISchedule) => {
      const description = this.scheduleFrequencyDescriptionFilter(schedule);
      if (!schedule.parallel) {
        return this.$translate.instant('serialScheduleDescription', {
          SERIAL_DESCRIPTION: description,
          SERIAL_DESCRIPTION_SUFFIX: this.$translate.instant('serialScheduleDescriptionSuffix')
        });
      }
      return description;
    };

    scope.displayScheduleDescriptionAsName = this.server.displayScheduleDescriptionAsName();

    return scope;
  }

  private createChangeSubscriptionsFrequencyScope(parentScope: ng.IScope,
    items: Set<ISubscriptionInfo>,
    options: IChangeSubscriptionsFrequencyOptions<ISubscriptionInfo>): IChangeSubscriptionsFrequencyScope {

    const scope = <IChangeSubscriptionsFrequencyScope>parentScope.$new();

    const subscription = items.first();
    const initialOnExtractRefresh: boolean = subscription.refreshExtractTriggered;
    const initialSelectedId = (initialOnExtractRefresh ? SubscriptionsFrequency.subscribeOnExtractRefresh : SubscriptionsFrequency.subscribeOnSelectedSchedule);

    scope.descriptionTranslateKey = options.descriptionTranslateKey;
    scope.descriptionTranslateValues = options.descriptionTranslateValues;

    scope.scheduleSource = this.SearchSuggestionsService.schedulesSource(options.scheduledActionType);
    scope.filterList = {
      model: null
    };

    scope.itemTextFn = (schedule: ISchedule) => {
      const description = this.scheduleFrequencyDescriptionFilter(schedule);
      if (!schedule.parallel) {
        return this.$translate.instant('serialScheduleDescription', {
          SERIAL_DESCRIPTION: description,
          SERIAL_DESCRIPTION_SUFFIX: this.$translate.instant('serialScheduleDescriptionSuffix')
        });
      }
      return description;
    };

    scope.displayScheduleDescriptionAsName = this.server.displayScheduleDescriptionAsName();

    scope.frequencyOptions = [
      { 'id': SubscriptionsFrequency.subscribeOnSelectedSchedule, 'label': this.$translate.instant('changeSubscriptionsFrequencyOptionOnSelectedSchedule') },
      { 'id': SubscriptionsFrequency.subscribeOnExtractRefresh, 'label': this.$translate.instant('changeSubscriptionsFrequencyOptionOnExtractRefresh') }
    ];
    scope.selected = {
      'id': initialSelectedId
    };

    return scope;
  }

  public getSelfServiceSchedulePaneProps(scheduleDetails: ISelfServiceScheduleDetails,
    timeZonesEnabled: boolean, timeZoneDisplayName: string, recurrenceType: string,
    type: SelfServiceSchedulePaneType): SelfServiceSchedulePaneProps {
    const details = this.createRecurrence(scheduleDetails, recurrenceType);
    const recurrence: Recurrence = {
      recurrenceMode: RecurrenceModes.TimeRecurrence,
      recurrenceType: recurrenceType,
      recurrenceDetails: details
    };

    const userAccountSettingsLink = new SiteUrls({
      siteUrlName: this.server.sessionSiteUrlName(),
      queryString: window.location.search
    }).userSettingsUrl(this.server.sessionUserInfo().username, this.server.sessionUserInfo().domainName);

    const timeZoneDetails: TimeZoneDetails = {
      timeZonesEnabled: timeZonesEnabled,
      timeZoneDisplayName: timeZoneDisplayName,
      linkToUserSettings: userAccountSettingsLink
    };

    const selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps = {
      recurrenceModel: recurrence,
      timeZoneDetails: timeZoneDetails,
      type: type,
      recurrenceModelChangedCallback: (model) => { }
    };

    return selfServiceSchedulePaneProps;
  }

  public setupSelfServiceScheduleDialogScope<T extends IChangeSelfServiceScheduleScope>(scope: T,
    scheduleDetails: ISelfServiceScheduleDetails, timeZonesEnabled: boolean,
    timeZoneDisplayName: string, recurrenceType: string, type: SelfServiceSchedulePaneType) {

    scope.selfServiceSchedulePaneProps = this.getSelfServiceSchedulePaneProps(
      scheduleDetails, timeZonesEnabled, timeZoneDisplayName, recurrenceType, type);
    scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback = (newRecurrence: Recurrence) => {
      scope.$apply(() => {
        scope.selfServiceSchedulePaneProps = {
          recurrenceModel: newRecurrence,
          timeZoneDetails: scope.selfServiceSchedulePaneProps.timeZoneDetails,
          recurrenceModelChangedCallback: scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback,
          type: type,
        };
      });
    };
  }

  private createSelfServiceScheduleDialogScope<T extends IScheduledItemInfo>(parentScope: ng.IScope,
    item: T,
    options: IChangeSelfServiceScheduleActionOptions<T>):
    IChangeSelfServiceScheduleScope {

    const scope = <IChangeSelfServiceScheduleScope>parentScope.$new();
    scope.descriptionTranslateKey = options.descriptionTranslateKey;
    scope.descriptionTranslateValues = options.descriptionTranslateValues;

    const schedule = item.schedule;
    const scheduleDetails = <ISelfServiceScheduleDetails>schedule.scheduleDetails;
    const userTimeZoneDisplay = this.server.userTimeZone().displayName;

    this.setupSelfServiceScheduleDialogScope(scope, scheduleDetails, FeatureFlags.userTimeZoneEnabled,
      userTimeZoneDisplay, schedule.scheduleType, SelfServiceSchedulePaneType.Default);
    return scope;
  }

  private createChangeSelfServiceSubscriptionsFrequencyScope<T extends IScheduledItemInfo>(parentScope: ng.IScope,
    subscription: ISubscriptionInfo,
    options: IChangeSelfServiceSubscriptionsFrequencyOptions<T>):
    IChangeSelfServiceSubscriptionsFrequencyScope {

    const scope = <IChangeSelfServiceSubscriptionsFrequencyScope>parentScope.$new();
    scope.descriptionTranslateKey = options.descriptionTranslateKey;
    scope.descriptionTranslateValues = options.descriptionTranslateValues;

    const schedule = subscription.schedule;
    const scheduleDetails = <ISelfServiceScheduleDetails>schedule.scheduleDetails;
    const userTimeZoneDisplay = this.server.userTimeZone().displayName;
    const initialOnExtractRefresh: boolean = subscription.refreshExtractTriggered;
    const initialSelectedId = (initialOnExtractRefresh ? SubscriptionsFrequency.subscribeOnExtractRefresh : SubscriptionsFrequency.subscribeOnSelectedSchedule);

    scope.frequencyOptions = [
      { 'id': SubscriptionsFrequency.subscribeOnSelectedSchedule, 'label': this.$translate.instant('changeSubscriptionsFrequencyOptionOnSelectedSchedule') },
      { 'id': SubscriptionsFrequency.subscribeOnExtractRefresh, 'label': this.$translate.instant('changeSubscriptionsFrequencyOptionOnExtractRefresh') }
    ];
    scope.selected = {
      'id': initialSelectedId
    };

    this.setupSelfServiceScheduleDialogScope(scope, scheduleDetails, FeatureFlags.userTimeZoneEnabled,
      userTimeZoneDisplay, schedule.scheduleType, SelfServiceSchedulePaneType.Default);
    return scope;
  }

  private createRecurrence(scheduleDetails: ISelfServiceScheduleDetails, recurrenceType: string): RecurrenceDetails {
    switch (recurrenceType) {
      case RecurrenceTypes.Hourly:
        return {
          startTime: scheduleDetails.startTime,
          endTime: scheduleDetails.endTime,
          recurrenceMinutes: scheduleDetails.recurrenceMinutes,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as HourlyRecurrence;
      case RecurrenceTypes.Daily:
        return {
          startTime: scheduleDetails.startTime,
          endTime: scheduleDetails.endTime,
          recurrenceMinutes: scheduleDetails.recurrenceMinutes,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as DailyRecurrence;
      case RecurrenceTypes.Weekly:
        return {
          startTime: scheduleDetails.startTime,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as WeeklyRecurrence;
      case RecurrenceTypes.Monthly:
        return {
          startTime: scheduleDetails.startTime,
          recurrenceDayOfMonth: scheduleDetails.recurrenceDayOfMonth,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as MonthlyRecurrence;
      default:
        return StaticInitializers.defaultHourly();
    }
  }

  private mixedScheduleTypesUnsupported(): ng.IPromise<IResult> {
    this.toaster.pop({ type: ToastType.Error, text: this.$translate.instant('mixedScheduleTypesUnsupported') });
    return null;
  }

  private executeSelfServiceSchedule<T extends IScheduledItemInfo>(
    items: Set<T>,
    context: IActionContext,
    options: IChangeSelfServiceScheduleActionOptions<T>
  ): ng.IPromise<IResult> {

    const scope = this.createSelfServiceScheduleDialogScope(context.scope, items.first(), options);

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: scope,
      bodyTemplateUrl: 'confirmActionDialogChangeSelfServiceSchedule.html',
      title: this.$translate.instant('changeSchedule'),
      actionName: this.$translate.instant('done'),
      size: '500px',
      action: () => {
        const schedule: SelfServiceSchedule = this.createSelfServiceScheduleFromRecurrenceModel(
          scope.selfServiceSchedulePaneProps.recurrenceModel);

        return options.action(items.keys(), schedule);
      }
    });

    dialogInstance.result
      .then(this.ActionHelpers.notifyStandardError(items, options.notificationTranslationIds,
        (errorItem: IErrorItem<T>) => options.errorDetailFn()(errorItem)));

    dialogInstance.result.finally(() => scope.$destroy());
    return dialogInstance.result;
  }

  private executeChangeSelfServiceSubscriptionsFrequency(
    subscriptions: Set<ISubscriptionInfo>,
    context: IActionContext,
    options: IChangeSelfServiceSubscriptionsFrequencyOptions<ISubscriptionInfo>
  ): ng.IPromise<IResult> {

    const subscription = subscriptions.first();
    const scope = this.createChangeSelfServiceSubscriptionsFrequencyScope(context.scope, subscription, options);

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: scope,
      bodyTemplateUrl: 'confirmActionDialogChangeSelfServiceSubscriptionFrequency.html',
      title: this.$translate.instant('changeSubscriptionsFrequency'),
      actionName: this.$translate.instant('done'),
      size: '500px',
      action: (dialogInstance: IConfirmActionDialogInstance) => {
        const schedule: SelfServiceSchedule = this.createSelfServiceScheduleFromRecurrenceModel(
          scope.selfServiceSchedulePaneProps.recurrenceModel);

        const selectedOnExtractRefresh: boolean = !!(scope.selected.id & SubscriptionsFrequency.subscribeOnExtractRefresh);
        // If we have chosen to subscribe based on an extract refresh, make a call to setSubscriptionsSchedule with following parameters:
        // subscription id, a Self Service Schedule based on the default Recurrence Model, and the value of subscribeOnExtractRefresh
        if (selectedOnExtractRefresh) {
          return this.Subscriptions.changeSelfServiceSubscriptionsFrequency(subscriptions.map(sub => sub.id), schedule, selectedOnExtractRefresh).then((
            result: IResult) => {
            if (result.errors && result.errors.length) {
              dialogInstance.showErrorMessage(
                this.$translate.instant('changeSubscriptionsFrequencyFailure'));
            } else {
              this.toaster.pop({
                type: ToastType.Info,
                text: this.$translate.instant('changeSubscriptionsFrequencySuccess'),
                testId: 'change-subscription-frequency-success'
              });
              subscription.refreshExtractTriggered = selectedOnExtractRefresh;
            }
            return result;
          });
        } else {
          options.action(subscriptions.keys(), schedule, selectedOnExtractRefresh);
        }
      }
    });

    dialogInstance.result
        .then(this.ActionHelpers.notifyStandardError(subscriptions, options.notificationTranslationIds,
            (errorItem: IErrorItem<ISubscriptionInfo>) => options.errorDetailFn()(errorItem)));

    dialogInstance.result.finally(() => scope.$destroy());
    return dialogInstance.result;
  }

  private execute<T>(
    items: Set<T>,
    context: IActionContext,
    options: IChangeAdminScheduleActionOptions<T>
  ): ng.IPromise<IResult> {

    const scope = this.createDialogScope(context.scope, items, options);

    const getSchedule = () => scope.filterList.model;

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: scope,
      bodyTemplateUrl: 'confirmActionDialogChangeSchedule.html',
      title: this.$translate.instant('changeSchedule'),
      actionName: this.$translate.instant('changeScheduleConfirm', { COUNT: items.size() }, L10n.Interpolations.Count),
      action: () => options.action(items.keys(), getSchedule().id),
      confirmDisabled: () => !getSchedule()
    });

    dialogInstance.result
      .then(this.ActionHelpers.notifyStandardError(items, options.notificationTranslationIds,
        (errorItem: IErrorItem<T>) => options.errorDetailFn(getSchedule())(errorItem)));

    dialogInstance.result.finally(() => scope.$destroy());
    return dialogInstance.result;
  }

  private executeChangeSubscriptionsFrequency(
    subscriptions: Set<ISubscriptionInfo>,
    context: IActionContext,
    options: IChangeSubscriptionsFrequencyOptions<ISubscriptionInfo>
  ): ng.IPromise<IResult> {

    const scope = this.createChangeSubscriptionsFrequencyScope(context.scope, subscriptions, options);

    const getSchedule = () => scope.filterList.model;

    const subscription = subscriptions.first();

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: scope,
      bodyTemplateUrl: 'confirmActionDialogChangeFrequency.html',
      title: this.$translate.instant('changeSubscriptionsFrequency'),
      actionName: this.$translate.instant('changeSubscriptionsFrequencyConfirm', { COUNT: subscriptions.size() }, L10n.Interpolations.Count),
      action: (dialogInstance: IConfirmActionDialogInstance) => {
        const selectedOnExtractRefresh: boolean = !!(scope.selected.id & SubscriptionsFrequency.subscribeOnExtractRefresh);
        // If we have chosen to subscribe based on an extract refresh, make a call to setSubscriptionsSchedule with following parameters:
        // subscription id, subscriptions *current* schedule id (to handle rollback scenario), and the value of subscribeOnExtractRefresh
        if (selectedOnExtractRefresh) {
          return this.Subscriptions.changeFrequency(subscriptions.map(sub => sub.id), subscription.schedule.id, selectedOnExtractRefresh).then((
            result: IResult) => {
            if (result.errors && result.errors.length) {
              dialogInstance.showErrorMessage(
                this.$translate.instant('changeSubscriptionsFrequencyFailure'));
            } else {
              this.toaster.pop({
                type: ToastType.Info,
                text: this.$translate.instant('changeSubscriptionsFrequencySuccess'),
                testId: 'change-subscription-frequency-success'
              });
              subscription.refreshExtractTriggered = selectedOnExtractRefresh;
            }
            return result;
          });
        } else {
          options.action(subscriptions.keys(), getSchedule().id, selectedOnExtractRefresh);
        }
      },
      // The confirm button should only be disabled if we have chosen to subscribe on a selected schedule, and we haven't yet
      // chosen a schedule
      confirmDisabled: () => !(scope.selected.id & SubscriptionsFrequency.subscribeOnExtractRefresh) && !getSchedule()
    });

    dialogInstance.result
      .then(this.ActionHelpers.notifyStandardError(subscriptions, options.notificationTranslationIds,
        (errorItem: IErrorItem<ISubscriptionInfo>) => options.errorDetailFn(getSchedule())(errorItem)));

    dialogInstance.result.finally(() => scope.$destroy());
    return dialogInstance.result;
  }

  private setScheduleErrorMessageFor(errorItem: IErrorItem<IScheduledItemInfo>, schedule: ISchedule) {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingScheduleNotFound:
        return this.$translate.instant('scheduleNotFound', { SCHEDULE: schedule.name });
    }
    return null;
  }

  public createSelfServiceScheduleFromRecurrenceModel(recurrenceModel: Recurrence): SelfServiceSchedule {

    const selfServiceSchedule: SelfServiceSchedule = {
      scheduledAction: <scheduledActionEnum>'Subscription',
      scheduleType: <scheduledTypeEnum>recurrenceModel.recurrenceType,
      scheduleDetails: recurrenceModel.recurrenceDetails as ScheduleDetails
    };

    return selfServiceSchedule;
  }

  private isMixedSchedulesTypes(subscriptions: Set<ISubscriptionInfo>): boolean {
    for (const abc of subscriptions.toArray()) {
      if (abc.schedule.definedBy !== subscriptions.first().schedule.definedBy) {
        return true;
      }
    }
    return false;
  }

  // Subscription specific logic

  public forSubscriptions(subscriptions: Set<ISubscriptionInfo>, context: IActionContext): ng.IPromise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (subscriptions.size() === 1) {
      const sub = subscriptions.first();
      descriptionTranslateValues = { TARGET_NAME: L10n.elideIfLong(sub.targetName) };
      if (sub.targetType === SubscriptionTargetTypes.view) {
        descriptionTranslateKey = 'changeScheduleSubscriptionViewPrompt';
      } else if (sub.targetType === SubscriptionTargetTypes.workbook) {
        descriptionTranslateKey = 'changeScheduleSubscriptionWorkbookPrompt';
      }
    } else {
      descriptionTranslateKey = 'changeScheduleSubscriptionsPrompt';
      descriptionTranslateValues = { COUNT: subscriptions.size() };
    }

    const options: IChangeScheduleActionOptions<ISubscriptionInfo> = {
      notificationTranslationIds: {
        successId: 'changeScheduleSubscriptionsSuccess',
        errorId: 'changeScheduleSubscriptionsError'
      },
      scheduledActionType: ScheduleActions.Subscription,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    if (this.isMixedSchedulesTypes(subscriptions)) {
      return this.mixedScheduleTypesUnsupported();
    }

    if (FeatureFlags.selfServiceSchedulesEnabled && subscriptions.first().schedule.definedBy === DefinedBy.SELF_SERVICE) {
      const SSSOptions: IChangeSelfServiceScheduleActionOptions<ISubscriptionInfo> = {
        action: (ids, schedule) => this.Subscriptions.setSelfServiceSchedule(ids, schedule),
        notificationTranslationIds: options.notificationTranslationIds,
        errorDetailFn: () => this.subscriptionErrorDetailFactorySSS(),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues,
      };
      return this.executeSelfServiceSchedule(subscriptions, context, SSSOptions);
    } else {
      const adminOptions: IChangeAdminScheduleActionOptions<ISubscriptionInfo> = {
        action: (ids, scheduleId) => this.Subscriptions.setSchedule(ids, scheduleId),
        notificationTranslationIds: options.notificationTranslationIds,
        errorDetailFn: (schedule) => this.subscriptionErrorDetailFactory(schedule),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues
      };
      return this.execute(subscriptions, context, adminOptions);
    }
  }

  public changeSubscriptionsFrequency(subscriptions: Set<ISubscriptionInfo>, context: IActionContext): ng.IPromise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (subscriptions.size() === 1) {
      const sub = subscriptions.first();
      descriptionTranslateValues = { TARGET_NAME: L10n.elideIfLong(sub.targetName) };
      if (sub.targetType === SubscriptionTargetTypes.view) {
        descriptionTranslateKey = 'changeScheduleSubscriptionViewPrompt';
      } else if (sub.targetType === SubscriptionTargetTypes.workbook) {
        descriptionTranslateKey = 'changeScheduleSubscriptionWorkbookPrompt';
      }
    } else {
      descriptionTranslateKey = 'changeScheduleSubscriptionsPrompt';
      descriptionTranslateValues = { COUNT: subscriptions.size() };
    }

    const options: IChangeScheduleActionOptions<ISubscriptionInfo> = {
      notificationTranslationIds: {
        successId: 'changeScheduleSubscriptionsSuccess',
        errorId: 'changeScheduleSubscriptionsError'
      },
      scheduledActionType: ScheduleActions.Subscription,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    if (this.isMixedSchedulesTypes(subscriptions)) {
      return this.mixedScheduleTypesUnsupported();
    }

    if (FeatureFlags.selfServiceSchedulesEnabled && subscriptions.first().schedule.definedBy === DefinedBy.SELF_SERVICE) {
      const SSSOptions: IChangeSelfServiceSubscriptionsFrequencyOptions<ISubscriptionInfo> = {
        action: (ids, schedule, subscribeOnExtractRefresh) =>
          this.Subscriptions.changeSelfServiceSubscriptionsFrequency(ids, schedule, subscribeOnExtractRefresh),
        notificationTranslationIds: options.notificationTranslationIds,
        errorDetailFn: () => this.subscriptionErrorDetailFactorySSS(),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues,
      };
      return this.executeChangeSelfServiceSubscriptionsFrequency(subscriptions, context, SSSOptions);
    } else {
      const adminOptions: IChangeSubscriptionsFrequencyOptions<ISubscriptionInfo> = {
        action: (ids, scheduleId, subscribeOnExtractRefresh) =>
          this.Subscriptions.changeFrequency(ids, scheduleId, subscribeOnExtractRefresh),
        notificationTranslationIds: options.notificationTranslationIds,
        errorDetailFn: (schedule) => this.subscriptionErrorDetailFactory(schedule),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues
      };
      return this.executeChangeSubscriptionsFrequency(subscriptions, context, adminOptions);
    }
  }

  private subscriptionErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<ISubscriptionInfo>): IErrorDetail => {
      return {
        itemName: errorItem.item.subject,
        errorMessage: this.subscriptionErrorMessageFor(errorItem, schedule)
      };
    };
  }

  private subscriptionErrorDetailFactorySSS() {
    return (errorItem: IErrorItem<ISubscriptionInfo>): IErrorDetail => {
      return {
        itemName: errorItem.item.subject,
        errorMessage: this.$translate.instant('changeSubscriptionScheduleFailure')
      };
    };
  }

  private subscriptionErrorMessageFor(errorItem: IErrorItem<ISubscriptionInfo>, schedule: ISchedule): string {
    switch (errorItem.error.code) {
      case CommonErrorCode.SubscriptionNotFound:
        return this.$translate.instant('subscriptionNotFound', { SUBSCRIPTION_SUBJECT: errorItem.item.subject });
    }
    return this.setScheduleErrorMessageFor(errorItem, schedule);
  }

  // Extract Tasks specific logic

  public forExtractTasks(extractTasks: Set<IExtractTaskInfo>, context: IActionContext): ng.IPromise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (extractTasks.size() === 1) {
      const task = extractTasks.first();
      if (task.workbook) {
        descriptionTranslateKey = 'changeScheduleExtractTasksWorkbookPrompt';
        descriptionTranslateValues = { PARENT_NAME: L10n.elideIfLong(task.workbook.name) };
      } else if (task.datasource) {
        descriptionTranslateKey = 'changeScheduleExtractTasksDatasourcePrompt';
        descriptionTranslateValues = { PARENT_NAME: L10n.elideIfLong(task.datasource.name) };
      }
    } else {
      descriptionTranslateKey = 'changeScheduleExtractTasksPrompt';
      descriptionTranslateValues = { COUNT: extractTasks.size() };
    }

    const options: IChangeAdminScheduleActionOptions<IExtractTaskInfo> = {
      action: (ids, scheduleId) => this.ExtractTasks.setSchedule(ids, scheduleId),
      notificationTranslationIds: {
        successId: 'changeScheduleExtractTasksSuccess',
        errorId: 'changeScheduleExtractTasksError'
      },
      errorDetailFn: (schedule) => this.extractTaskErrorDetailFactory(schedule),
      scheduledActionType: ScheduleActions.Extract,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    return this.execute(extractTasks, context, options);
  }

  private extractTaskErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IExtractTaskInfo>): IErrorDetail => {
      const namedItem: INamedItemInfo = errorItem.item.datasource || errorItem.item.workbook;

      return {
        itemName: namedItem.name,
        errorMessage: this.extractTaskErrorMessageFor(errorItem, schedule)
      };
    };
  }

  private extractTaskErrorMessageFor(errorItem: IErrorItem<IExtractTaskInfo>, schedule: ISchedule): string {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingTaskNotFound:
        if (errorItem.item.workbook)
          return this.$translate.instant('extractTaskNotFoundForWorkbook', { WORKBOOK_NAME: L10n.elideIfLong(errorItem.item.workbook.name) });
        if (errorItem.item.datasource)
          return this.$translate.instant('extractTaskNotFoundForDatasource', { DATASOURCE_NAME: L10n.elideIfLong(errorItem.item.datasource.name) });
    }
    return this.setScheduleErrorMessageFor(errorItem, schedule);
  }

  public forRunFlowTasks(runFlowTaskInfos: Set<RunFlowTaskInfo>, context: IActionContext): ng.IPromise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (runFlowTaskInfos.size() === 1) {
      const task = runFlowTaskInfos.first();
      descriptionTranslateKey = 'changeScheduleRunFlowTaskPrompt';
      descriptionTranslateValues = { FLOW_NAME: L10n.elideIfLong(task.flow.name) };
    } else {
      descriptionTranslateKey = 'changeScheduleRunFlowTasksPrompt';
      descriptionTranslateValues = { COUNT: runFlowTaskInfos.size() };
    }

    const options: IChangeAdminScheduleActionOptions<RunFlowTaskInfo> = {
      action: (ids, scheduleId) => this.RunFlowTasks.setSchedule(ids, scheduleId),
      notificationTranslationIds: {
        successId: 'changeScheduleRunFlowTasksSuccess',
        errorId: 'changeScheduleRunFlowTasksError'
      },
      errorDetailFn: (schedule) => this.runFlowTaskErrorDetailFactory(schedule),
      scheduledActionType: ScheduleActions.Flow,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    return this.execute(runFlowTaskInfos, context, options);
  }

  private runFlowTaskErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<RunFlowTaskInfo>): IErrorDetail => {
      const namedItem: INamedItemInfo = errorItem.item.flow;
      return {
        itemName: namedItem.name,
        errorMessage: this.runFlowTaskErrorMessageFor(errorItem, schedule)
      };
    };
  }

  private runFlowTaskErrorMessageFor(errorItem: IErrorItem<RunFlowTaskInfo>, schedule: ISchedule): string {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingTaskNotFound:
        return this.$translate.instant('runFlowTaskNotFoundForFlow', { FLOW_NAME: L10n.elideIfLong(errorItem.item.flow.name) });
    }
    return this.setScheduleErrorMessageFor(errorItem, schedule);
  }

}

Module.instance().service('ChangeScheduleAction', ChangeScheduleAction);
